/**
 * EncryptionPanel (Welle 7a)
 *
 * Aufklappbares Verschlüsselungs-Detail-Panel für den Berater. Wird über ein
 * Schloss-Icon in der Call-Leiste geöffnet. Zeigt Verbindungstyp (direkt vs.
 * Relay), DTLS-Version, Medien-Cipher, RTT und perspektivisch SFrame-Status.
 *
 * Datenquelle: rtc.connectionInfo aus getStats — keine Server-Beteiligung.
 */

import { useState, type CSSProperties } from 'react';
import type { ConnectionInfo } from '@/hooks/connectionStats';
import type { PeerStats, QualityLevel } from '@/hooks/useWebRtc';

// pre166/167: Telemetrie (Verbindungsqualität) lebt jetzt im Detail-Menü dieser
// zusammengefassten Badge — volle Kennzahlen je Strecke + RTT-Sparkline.
function levelColor(l: QualityLevel | null | undefined): string {
  return l === 'bad' ? 'var(--danger)' : l === 'fair' ? 'var(--warning)' : l === 'good' ? 'var(--status-available)' : 'rgba(255,255,255,0.45)';
}

function MiniSpark({ history, level }: { history: number[]; level: QualityLevel }) {
  const color = level === 'bad' ? 'var(--danger)' : level === 'fair' ? 'var(--warning)' : 'var(--status-available)';
  if (history.length < 2) {
    return <div className="ts2-spark ts2-spark-empty">RTT · sammelt Daten…</div>;
  }
  const min = Math.min(...history);
  const max = Math.max(...history);
  const range = max - min || 1;
  const step = 240 / (history.length - 1);
  const pts = history.map((v, i) => `${(i * step).toFixed(1)},${(28 - ((v - min) / range) * 24).toFixed(1)}`).join(' ');
  return (
    <div className="ts2-spark">
      <div className="ts2-spark-head">
        <span>RTT · {history.length * 3}s</span>
        <span>min {Math.round(min)} · max {Math.round(max)} ms</span>
      </div>
      <svg viewBox="0 0 240 28" preserveAspectRatio="none">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
      </svg>
    </div>
  );
}

function SideBlock({ name, hint, s, history }: { name: string; hint: string; s: PeerStats | null | undefined; history: number[] }) {
  const dot = !s ? '' : s.level === 'bad' ? 'bad' : s.level === 'fair' ? 'warn' : 'ok';
  const rttCls = s && s.rttMs != null && s.rttMs > 500 ? 'bad' : s && s.rttMs != null && s.rttMs > 250 ? 'warn' : '';
  const jitCls = s && s.jitterMs != null && s.jitterMs > 50 ? 'bad' : s && s.jitterMs != null && s.jitterMs > 30 ? 'warn' : '';
  const lossCls = s && s.lossPct >= 3 ? 'bad' : s && s.lossPct >= 1 ? 'warn' : '';
  return (
    <div className="ts2-side">
      <div className="ts2-head">
        <span className={`ts2-dot ${dot}`}></span>
        <span className="ts2-name">{name}</span>
        <span className="ts2-hint">{hint}</span>
      </div>
      {s ? (
        <>
          <div className="ts2-metrics">
            <div className="ts2-m"><div className="l">RTT</div><div className={`v ${rttCls}`}>{s.rttMs ?? '—'}<small>ms</small></div></div>
            <div className="ts2-m"><div className="l">Jitter</div><div className={`v ${jitCls}`}>{s.jitterMs ?? '—'}<small>ms</small></div></div>
            <div className="ts2-m"><div className="l">Verlust</div><div className={`v ${lossCls}`}>{s.lossPct}<small>%</small></div></div>
          </div>
          <div className="ts2-our">{s.width}×{s.height} · {s.rxFps} fps · {s.codec} · Freezes {s.freezeCount}</div>
          <MiniSpark history={history} level={s.level} />
        </>
      ) : (
        <div className="ts2-wait">— wartet auf Daten —</div>
      )}
    </div>
  );
}

// pre168: Verbindungsqualität als Signalbalken — je Seite 3 Balken; die Anzahl
// leuchtender Balken codiert das Level (gut=3, mittel=2, schwach=1), zusätzlich zur
// Farbe (also auch ohne Farbsehen unterscheidbar). Links = Berater, rechts = Stele.
function NetBars({ a, b }: { a?: QualityLevel | null; b?: QualityLevel | null }) {
  const lit = (l?: QualityLevel | null) => (l === 'good' ? 3 : l === 'fair' ? 2 : l === 'bad' ? 1 : 0);
  const dim = 'rgba(255,255,255,0.22)';
  const bars = [{ x: 0, y: 9, h: 4 }, { x: 3.5, y: 5.5, h: 7.5 }, { x: 7, y: 2, h: 11 }];
  const group = (ox: number, l?: QualityLevel | null) => {
    const n = lit(l); const c = levelColor(l);
    return bars.map((bz, i) => (
      <rect key={`${ox}-${i}`} x={ox + bz.x} y={bz.y} width={2.4} height={bz.h} rx={0.6} fill={i < n ? c : dim} />
    ));
  };
  return (
    <svg width="34" height="16" viewBox="0 0 34 16" style={{ flexShrink: 0 }} aria-label="Verbindungsqualität Berater und Stele">
      <title>{`Netz — Berater: ${a ?? '…'} · Stele: ${b ?? '…'}`}</title>
      {group(3, a)}
      {group(20, b)}
    </svg>
  );
}

export function EncryptionBadge({ info, label, timer, statsAgent, statsStele, rttHistoryAgent, rttHistoryStele, meter }: {
  info: ConnectionInfo | null;
  /** pre244: Mini-Pegelanzeige der Gegenseite, rechts neben dem Verschlüsselungs-Status. */
  meter?: React.ReactNode;
  label?: string;
  timer?: string;
  statsAgent?: PeerStats | null;
  statsStele?: PeerStats | null;
  rttHistoryAgent?: number[];
  rttHistoryStele?: number[];
}) {
  const [menu, setMenu] = useState<null | 'tele' | 'enc'>(null);
  const merged = typeof label === 'string';

  // WebRTC ist immer DTLS-SRTP-verschlüsselt sobald verbunden — das "gesichert"-
  // Flag basiert daher auf dem Verbindungszustand, nicht auf der Cipher-Anzeige.
  const secured = Boolean(info?.connected);
  const direct = info?.transport === 'direct';
  const grade = gradeCipher(info);

  // Welle 8d/M2: Anzeige nach schwächstem Teilnehmer. Ist ein Experte zugeschaltet
  // (3+), pausiert SFrame → die Strecke ist dann NUR transportverschlüsselt. Die Badge
  // unterscheidet sichtbar zwischen Ende-zu-Ende (grün) und transportverschlüsselt
  // (bernstein) statt pauschal „Verschlüsselt" zu zeigen.
  const e2ee = Boolean(info?.e2eeActive);
  const tone: 'e2e' | 'transport' | 'pending' = !secured ? 'pending' : e2ee ? 'e2e' : 'transport';
  const palette = {
    e2e: { bg: 'rgba(29,158,117,0.15)', fg: '#0F6E56', label: 'Ende-zu-Ende' },
    transport: { bg: 'rgba(186,117,23,0.18)', fg: '#854F0B', label: 'Transportverschlüsselt' },
    pending: { bg: 'rgba(220,90,48,0.15)', fg: '#993C1D', label: 'Verbindet…' },
  }[tone];
  // pre165: Badge sitzt jetzt oben auf dem (hellen) Video → dunkler Glas-Pill mit
  // hellem, gut lesbarem Text statt hellem Hintergrund mit dunkler Schrift.
  const badgeText = tone === 'e2e' ? '#34d399' : tone === 'transport' ? '#fbbf24' : '#fca5a5';

  const lockPath = tone === 'pending'
    ? (<><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></>)
    : (<><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>);
  const ddStyle: CSSProperties = {
    position: 'absolute', top: 'calc(100% + 8px)', width: 300,
    background: 'var(--surface, #1a1a1a)', color: 'var(--text, #fff)',
    border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
    padding: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.4)', zIndex: 1000,
  };
  const partBtn: CSSProperties = {
    background: 'transparent', border: 'none', padding: 0, font: 'inherit',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', borderRadius: 999, background: '#0a0a0aa6',
        WebkitBackdropFilter: 'blur(20px)', backdropFilter: 'blur(20px)',
        color: merged ? '#fff' : badgeText, border: '1px solid rgba(255,255,255,0.12)',
        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      }}>
        {merged && (
          <>
            <button onClick={() => setMenu((m) => (m === 'tele' ? null : 'tele'))} title="Verbindungsqualität"
              style={{ ...partBtn, color: 'inherit', gap: 8 }}>
              <NetBars a={statsAgent?.level} b={statsStele?.level} />
              <span style={{ letterSpacing: '.06em', textTransform: 'uppercase', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
              {meter && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{meter}</span>}
              {timer && <span style={{ opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{timer}</span>}
            </button>
            <span style={{ width: 1, height: 13, background: 'rgba(255,255,255,0.18)' }} />
          </>
        )}
        <button onClick={() => setMenu((m) => (m === 'enc' ? null : 'enc'))} title="Verschlüsselungs-Details"
          style={{ ...partBtn, color: badgeText, gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={badgeText} strokeWidth="2">
            {lockPath}
          </svg>
          <span>{palette.label}</span>
        </button>
      </div>

      {menu && <div onClick={() => setMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />}

      {menu === 'tele' && merged && (
        <div style={{ ...ddStyle, left: 0 }}>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Verbindungsqualität</div>
          <SideBlock name="Berater" hint="empfängt Gast" s={statsAgent} history={rttHistoryAgent ?? []} />
          <SideBlock name="Stele" hint="empfängt Berater" s={statsStele} history={rttHistoryStele ?? []} />
          <div className={`tel-loc-compact ${(statsAgent?.relay || statsStele?.relay) ? 'warn' : ''}`} style={{ marginTop: 8 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            <span>{(statsAgent?.relay || statsStele?.relay) ? 'TURN-Relay aktiv' : 'Direktverbindung (P2P)'}</span>
          </div>
        </div>
      )}

      {menu === 'enc' && (
        <div style={{ ...ddStyle, right: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%',
              background: tone === 'e2e' ? 'rgba(29,158,117,0.2)' : tone === 'transport' ? 'rgba(186,117,23,0.2)' : 'rgba(220,90,48,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: tone === 'e2e' ? '#1D9E75' : tone === 'transport' ? '#BA7517' : '#D85A30',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {tone === 'e2e' ? 'Ende-zu-Ende verschlüsselt' : tone === 'transport' ? 'Transportverschlüsselt' : 'Verbindung wird aufgebaut'}
              </div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>
                {tone === 'e2e' ? 'SFrame über DTLS-SRTP — Schlüssel nur auf den Endgeräten' : 'DTLS-SRTP (Transport)'}
              </div>
            </div>
          </div>

          {/* Verbindungstyp-Banner */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '7px 9px',
            borderRadius: 6, marginBottom: 12, fontSize: 12, fontWeight: 600,
            background: direct ? 'rgba(29,158,117,0.15)' : 'rgba(186,117,23,0.18)',
            color: direct ? '#0F6E56' : '#854F0B',
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17l-4-4 4-4M17 7l4 4-4 4M3 13h18" />
            </svg>
            {info?.transport === 'direct' && 'Direkte Verbindung zur Stele'}
            {info?.transport === 'relay' && 'Über gesicherten Relay (verschlüsselt)'}
            {(!info || info.transport === 'unknown') && 'Verbindung wird aufgebaut…'}
          </div>

          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Verbindungstyp" value={
                info?.localCandidateType && info?.remoteCandidateType
                  ? `${info.localCandidateType} ↔ ${info.remoteCandidateType}`
                  : '—'
              } />
              <Row label="Transport" value={
                info?.tlsVersion ? `DTLS ${info.tlsVersion}`
                  : secured ? 'DTLS-SRTP (aktiv)'
                  : '—'
              } />
              <Row label="Medien-Cipher" value={
                info?.srtpCipher ?? info?.dtlsCipher
                  ?? (secured ? 'ausgehandelt' : '—')
              } mono={info?.cipherKnown} />
              <CipherGradeRow info={info} secured={secured} />
              <E2eeRow info={info} secured={secured} />
              <Row label="Videocodec" value={info?.videoCodec ?? '—'} mono={!!info?.videoCodec} />
              <Row label="Auflösung" value={info?.frameWidth && info?.frameHeight ? `${info.frameWidth}×${info.frameHeight}` : '—'} mono={!!info?.frameWidth} />
              <Row label="Audiocodec" value={info?.audioCodec ?? '—'} mono={!!info?.audioCodec} />
              <Row label="Latenz (RTT)" value={info?.rttMs != null ? `${info.rttMs} ms` : '—'} />
            </tbody>
          </table>

          {info?.e2eeActive && (
            <div style={{ marginTop: 10, fontSize: 10.5, opacity: 0.6, lineHeight: 1.5 }}>
              Zusätzlich zur Transport-Verschlüsselung sind die Medien Ende-zu-Ende
              mit AES-256-GCM gesichert. Der Schlüssel wurde direkt zwischen den
              Endgeräten ausgehandelt (ECDH) und ist dem Server nicht bekannt.
            </div>
          )}
          {/* Welle 7e: Warnhinweis, wenn E2EE NICHT aktiv ist (Mehrparteien/SFU/MCU
              oder Policy aus). Transport bleibt verschlüsselt, aber nicht Ende-zu-Ende. */}
          {secured && (info?.e2eeIntended === false || info?.e2eeSuspended) && (
            <div style={{ marginTop: 10, fontSize: 10.5, lineHeight: 1.5,
              color: '#854F0B', background: 'rgba(186,117,23,0.12)', padding: '7px 9px', borderRadius: 6 }}>
              ⚠ {info?.e2eeSuspended
                ? 'Ende-zu-Ende-Verschlüsselung pausiert, weil mehr als zwei Teilnehmer im Gespräch sind. Sie wird automatisch wieder aktiv, sobald wieder nur zwei verbunden sind.'
                : 'Keine Ende-zu-Ende-Verschlüsselung in diesem Gespräch (z.B. Mehrparteien-/SFU-/MCU-Modus oder Mandanten-Policy „aus").'}
              {' '}Die Verbindung bleibt per TLS/DTLS-SRTP transportverschlüsselt.
            </div>
          )}
          {secured && !info?.e2eeActive && info?.e2eeIntended && grade === 'standard' && (
            <div style={{ marginTop: 10, fontSize: 10.5, opacity: 0.6, lineHeight: 1.5 }}>
              E2EE wird gerade ausgehandelt — die Medien-Verschlüsselung (AES-256-GCM)
              schaltet sich automatisch zu, sobald beide Seiten den Schlüssel bestätigt haben.
            </div>
          )}
          {secured && !info?.cipherKnown && (
            <div style={{ marginTop: 10, fontSize: 10.5, opacity: 0.55, lineHeight: 1.5 }}>
              Dieser Browser weist die Cipher-Details nicht aus. Die Medien sind
              dennoch DTLS-SRTP-verschlüsselt — das ist bei WebRTC immer aktiv.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type CipherGrade = 'strong' | 'standard' | 'unknown';

function gradeCipher(info: ConnectionInfo | null): CipherGrade {
  if (!info?.cipherKnown) return 'unknown';
  if (info.cipherStrong) return 'strong'; // AES-GCM / ChaCha20
  return 'standard'; // bekannt, aber nicht GCM (z.B. AES_CM_128_HMAC_SHA1)
}

function CipherGradeRow({ info, secured }: { info: ConnectionInfo | null; secured: boolean }) {
  if (!secured) return <Row label="Cipher-Stärke" value="—" />;
  // Welle 7c: bei aktiver E2EE ist die effektive Stärke AES-256-GCM (SFrame),
  // unabhängig von der ausgehandelten Transport-Cipher.
  const grade: CipherGrade = info?.e2eeActive ? 'strong' : gradeCipher(info);
  const cfg: Record<CipherGrade, { text: string; bg: string; fg: string }> = {
    strong:   { text: info?.e2eeActive ? 'Stark · E2EE AES-256-GCM' : 'Stark · AES-GCM', bg: 'rgba(29,158,117,0.18)', fg: '#0F6E56' },
    standard: { text: 'Standard · AES-128', bg: 'rgba(186,117,23,0.20)', fg: '#854F0B' },
    unknown:  { text: 'nicht ausgewiesen',  bg: 'rgba(136,135,128,0.2)', fg: '#5F5E5A' },
  };
  const c = cfg[grade];
  return (
    <tr style={{ borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
      <td style={{ padding: '5px 0', opacity: 0.65 }}>Cipher-Stärke</td>
      <td style={{ padding: '5px 0', textAlign: 'right' }}>
        <span style={{
          fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
          background: c.bg, color: c.fg,
        }}>{c.text}</span>
      </td>
    </tr>
  );
}

/** Welle 7e: E2EE-Zeile mit drei ehrlichen Zuständen. */
function E2eeRow({ info, secured }: { info: ConnectionInfo | null; secured: boolean }) {
  let text: string; let bg: string; let fg: string;
  if (info?.e2eeActive) {
    text = 'AES-256-GCM (SFrame) aktiv'; bg = 'rgba(29,158,117,0.18)'; fg = '#0F6E56';
  } else if (info?.e2eeSuspended) {
    text = 'pausiert (Mehrparteien)'; bg = 'rgba(186,117,23,0.20)'; fg = '#854F0B';
  } else if (secured && info?.e2eeIntended === false) {
    text = 'kein E2EE — nur Transport'; bg = 'rgba(186,117,23,0.20)'; fg = '#854F0B';
  } else if (secured && info?.e2eeIntended) {
    text = 'wird ausgehandelt…'; bg = 'rgba(136,135,128,0.2)'; fg = '#5F5E5A';
  } else {
    text = '—'; bg = 'transparent'; fg = '#5F5E5A';
  }
  return (
    <tr style={{ borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
      <td style={{ padding: '5px 0', opacity: 0.65 }}>E2EE-Schicht</td>
      <td style={{ padding: '5px 0', textAlign: 'right' }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: bg, color: fg }}>
          {text}
        </span>
      </td>
    </tr>
  );
}

function Row({ label, value, mono, badge }: { label: string; value: string; mono?: boolean; badge?: boolean }) {
  return (
    <tr style={{ borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
      <td style={{ padding: '5px 0', opacity: 0.65 }}>{label}</td>
      <td style={{
        padding: '5px 0', textAlign: 'right',
        fontFamily: mono ? 'var(--font-mono, monospace)' : 'inherit',
        fontSize: mono ? 11 : 12,
      }}>
        {badge
          ? <span style={{ fontSize: 10, opacity: 0.55, fontStyle: 'italic' }}>{value}</span>
          : value}
      </td>
    </tr>
  );
}
